<template>
  <div class="chart">
    <div class="flex flex-wrap -mx-4 items-end">
      <div :class="[barWidthClass, containerOffsetClass]" />
      <div
        v-if="showLohnfortzahlung"
        :class="barWidthClass"
      />
      <div :class="barWidthClass">
        <div class="info info--wide">
          Das Krankentagegeld schließt die Lücke
        </div>
      </div>
      <div :class="buInfoWidthClass">
        <div class="info info--narrow">
          Die Berufsunfähig&shy;keitsversicherung schließt die Lücke
        </div>
      </div>
    </div>

    <div class="graph flex flex-wrap -mx-4">
      <Bar
        :height="barHeight"
        :class="[barWidthClass, containerOffsetClass]"
      >
        <label :class="$style.ieOverflowFix" for="monatliches-nettoeinkommen" class="mb-2">
          Monatliches Netto&shy;einkommen
        </label>
        <popover
          :class="$style.ieOverflowFix"
          :options="{placement: 'left-start'}"
          title="Hinweis!"
        >
          <span>
            Schreiben Sie Ihr Nettoeinkommen in das vorgegebene Feld
          </span>
          <template #reference>
            <slot />
          </template>
        </popover>
      </Bar>
      <Bar
        v-if="showLohnfortzahlung"
        :height="barHeight"
        :class="barWidthClass"
      >
        Lohn&shy;fortzahlung durch Arbeit&shy;geber 100 % des Netto&shy;einkommens
      </Bar>
      <Bar
        :height="barHeight"
        :class="barWidthClass"
        :ratio="ratio.krankentagegeldLuecke"
      >
        <template #stack>
          <span>Versorgungs&shy;lücke</span>
          <span
            v-show="isUpdating"
            class="spinner"
          />
          <span v-show="!isUpdating">{{ format(krankentagegeldLuecke) }}</span>
        </template>

        <span>Krankengeld (GKV)</span>
        <span
          v-show="isUpdating"
          class="spinner spinner--white"
        />
        <span v-show="!isUpdating">{{ format(krankentagegeld) }}</span>
      </Bar>
      <Bar
        v-if="showErwerbsminderungsrente"
        :height="barHeight"
        :class="barWidthClass"
        :ratio="ratio.erwerbsrenteLuecke"
      >
        <template #stack>
          <span>Versorgungs&shy;lücke</span>
          <span
            v-show="isUpdating"
            class="spinner"
          />
          <span v-show="!isUpdating">{{ format(erwerbsminderungsrenteVollLuecke) }}</span>
        </template>

        <span>Volle Erwerbs&shy;minderungs&shy;rente {{ legendMark('volle-rente') }}</span>
        <span
          v-show="isUpdating"
          class="spinner spinner--white"
        />
        <span v-show="!isUpdating">{{ format(erwerbsminderungsrenteVoll) }}</span>
      </Bar>
      <Bar
        v-if="showErwerbsminderungsrente"
        :height="barHeight"
        :class="barWidthClass"
        :ratio="ratio.erwerbsrenteHalbLuecke"
      >
        <template #stack>
          <span>Versorgungs&shy;lücke</span>
          <span
            v-show="isUpdating"
            class="spinner"
          />
          <span v-show="!isUpdating">{{ format(erwerbsminderungsrenteHalbLuecke) }}</span>
        </template>

        <span>Halbe Erwerbs&shy;minderungs&shy;rente {{ legendMark('halbe-rente') }}</span>
        <span
          v-show="isUpdating"
          class="spinner spinner--white"
        />
        <span v-show="!isUpdating">{{ format(erwerbsminderungsrenteHalb) }}</span>
      </Bar>
      <Bar
        :height="barHeight"
        :class="barWidthClass"
        :ratio="ratio.berufsunfaehigkeitLuecke"
      >
        <template #stack>
          <span>Versorgungs&shy;lücke</span>
          <span
            v-show="isUpdating"
            class="spinner"
          />
          <span v-show="!isUpdating">{{ format(money) }}</span>
        </template>
      </Bar>
    </div>

    <div class="flex flex-wrap -mx-4">
      <div
        :class="[maxColumnsUsedClass, containerOffsetClass]"
        class="line mb-5"
      />
    </div>

    <div class="flex flex-wrap -mx-4 mb-2.5">
      <div :class="[barWidthClass, containerOffsetClass]" />
      <div
        v-if="showLohnfortzahlung"
        :class="barWidthClass"
      >
        <div class="double-arrow">
          Max. 6 Wochen
        </div>
      </div>
      <div :class="barWidthClass">
        <div
          class="double-arrow"
          style="white-space: nowrap;"
        >
          Max. 72 Wochen {{ legendMark('krankengeld') }}
        </div>
      </div>
    </div>
    <div class="flex flex-wrap -mx-4">
      <div :class="[barWidthClass, containerOffsetClass]" />
      <div :class="krankheitRangeWidthClass">
        <div class="double-arrow">
          Bei Krankheit
        </div>
      </div>
      <div
        v-if="showErwerbsminderungsrente"
        :class="erwerbsminderungWidthClass"
      >
        <div class="double-arrow">
          Bei Erwerbsunfähigkeit
        </div>
      </div>

      <div :class="barWidthClass">
        <div class="double-arrow">
          Bei Berufsunfähigkeit
        </div>
      </div>
    </div>

    <div class="flex flex-wrap -mx-4">
      <div
        class="flex-1 px-4"
        :class="containerOffsetClass"
      >
        <div class="mt-5 flex flex-col">
          <small v-if="visibleLegends.includes('volle-rente')">
            {{ legendMark('volle-rente') }}
            Volle Erwerbs&shy;minderungs&shy;rente liegt vor bei unter 3 Stunden pro Tag Arbeitsfähigkeit
          </small>
          <small v-if="visibleLegends.includes('halbe-rente')">
            {{ legendMark('halbe-rente') }}
            Halbe Erwerbs&shy;minderungs&shy;rente liegt vor bei 3 bis 6 Stunden pro Tag Arbeitsfähigkeit
          </small>
          <small v-if="visibleLegends.includes('krankengeld')">
            {{ legendMark('krankengeld') }}
            Die Bezugsdauer des Krankengeldes reduziert sich um die Dauer der Lohnfortzahlung durch den Arbeitgeber
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Popover from '@/application/components/Popover.vue';
import Bar from '@/application/utils/chart/ChartBar.vue';
import { format } from '@/application/utils/money';

import { props } from './props';

const GRID_SIZE = 12;

// Spaltenbreiten und -versaetze als feste Klassennamen: Tailwind scannt den
// Quelltext statisch, zusammengesetzte Namen wuerden nicht erzeugt.
const WIDTH_CLASSES = [
  '', 'w-1/12', 'w-1/6', 'w-1/4', 'w-1/3', 'w-5/12', 'w-1/2',
  'w-7/12', 'w-2/3', 'w-3/4', 'w-5/6', 'w-11/12', 'w-full',
];

const OFFSET_CLASSES = [
  'ml-0', 'ml-[8.333333%]', 'ml-[16.666667%]', 'ml-[25%]', 'ml-[33.333333%]',
  'ml-[41.666667%]', 'ml-[50%]', 'ml-[58.333333%]', 'ml-[66.666667%]',
  'ml-[75%]', 'ml-[83.333333%]', 'ml-[91.666667%]',
];

// `max-w-*` haelt die Balken auf ihrer Spaltenbreite: ChartBar setzt in seinem
// eigenen (ungelayerten) Style `flex: 1 0 calc(20% - 24px)` und schlaegt damit
// die Breite aus der Utility-Layer.
const MAX_WIDTH_CLASSES = [
  '', 'max-w-[8.333333%]', 'max-w-[16.666667%]', 'max-w-[25%]', 'max-w-[33.333333%]',
  'max-w-[41.666667%]', 'max-w-[50%]', 'max-w-[58.333333%]', 'max-w-[66.666667%]',
  'max-w-[75%]', 'max-w-[83.333333%]', 'max-w-[91.666667%]', 'max-w-full',
];

const column = (width) => [WIDTH_CLASSES[width], MAX_WIDTH_CLASSES[width], 'px-4'];

export default {
  components: {
    Popover,
    Bar,
  },

  props,

  data() {
    return {
      barHeight: 320,
    };
  },

  computed: {
    ratio() {
      return {
        krankentagegeldLuecke: (this.krankentagegeldLuecke / this.money) * 100,
        erwerbsrenteLuecke: (this.erwerbsminderungsrenteVollLuecke / this.money) * 100,
        erwerbsrenteHalbLuecke: (this.erwerbsminderungsrenteHalbLuecke / this.money) * 100,
        berufsunfaehigkeitLuecke: 100,
      };
    },

    columnCount() {
      let columns = 3;
      if (this.showLohnfortzahlung) {
        columns += 1;
      }
      if (this.showErwerbsminderungsrente) {
        columns += 2;
      }
      return columns;
    },

    colWidth() {
      return Math.floor(GRID_SIZE / this.columnCount);
    },

    maxColumnsUsed() {
      return this.columnCount * this.colWidth;
    },

    maxColumnsUsedClass() {
      return column(this.maxColumnsUsed);
    },

    containerOffsetClass() {
      // get unused columns of GRID_SIZE to offset the grid by a half, so it is center
      return OFFSET_CLASSES[Math.floor((GRID_SIZE - this.maxColumnsUsed) / 2)];
    },

    barWidthClass() {
      return column(this.colWidth);
    },

    buInfoWidthClass() {
      const colNumber = this.showErwerbsminderungsrente ? 3 : 1;
      return column(this.colWidth * colNumber);
    },

    erwerbsminderungWidthClass() {
      return column(this.colWidth * 2);
    },

    krankheitRangeWidthClass() {
      const colNumber = this.showLohnfortzahlung ? 2 : 1;
      return column(this.colWidth * colNumber);
    },

    visibleLegends() {
      const visible = [];
      if (this.showErwerbsminderungsrente) {
        visible.push('volle-rente', 'halbe-rente');
      }
      visible.push('krankengeld');
      return visible;
    },
  },

  methods: {
    format,
    legendMark(id) {
      const i = this.visibleLegends.indexOf(id);
      if (i < 0) {
        return '*';
      }
      return '*'.repeat(i + 1);
    },
  },
};
</script>

<style lang="scss" module>

  $white: #FFF;
  $black: #000;

  .ieOverflowFix {
    max-width: 100%;
  }
</style>

<style lang="scss" scoped>

  $white: #FFF;
  $black: #000;
  $blue: #4181D2;
  $blue-light: #D8E7F7;

  .line {
    border-bottom: 2px solid $black;
  }

  .double-arrow {
    border-top: 2px solid $black;
    font-size: 90%;
    margin-left: 5px;
    margin-right: 5px;
    padding: 5px 0;
    position: relative;
    text-align: center;

    &::before,
    &::after {
      border: 5px solid transparent;
      border-left-width: 10px;
      border-right-width: 10px;
      content: '';
      display: block;
      position: absolute;
      top: -6px;
    }

    &::before {
      border-left-color: $black;
      right: -15px;
    }

    &::after {
      border-right-color: $black;
      left: -15px;
    }
  }

  .info {
    background: $white;
    border: 2px solid $blue;
    display: inline-block;
    font-size: 90%;
    left: -3%;
    padding: 5px;
    position: relative;
    text-align: center;
    transform: translateX(5px) rotateZ(-3deg);
    width: 106%;
    z-index: 1;

    &--wide {
      left: -20%;
      width: 130%;
    }

    &--narrow {
      left: 3%;
      width: 90%;
    }
  }

  .stack {
    display: flex;
    flex-direction: column;
    font-size: 90%;
  }
</style>
